import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { initSentry } from "@/instrument";
import "@/index.css";
import "@/hx/hx.css";
import App from "@/App";

// Production console guard — no-op `console.log`/`info`/`debug` in prod so
// dev diagnostics stay clean of the shipping bundle. `console.warn` and
// `console.error` are preserved because they're captured as Sentry breadcrumbs
// and are used for real observability.
if (process.env.NODE_ENV === "production") {
  const noop = () => {};
  console.log   = noop;
  console.info  = noop;
  console.debug = noop;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

function ErrorFallback({ error, componentStack, resetError }) {
  // Show the actual error text in non-production so we can debug the recurring
  // "Something broke on our end." reports. In prod we keep it opaque.
  const isProd = process.env.NODE_ENV === "production";
  const message = (error && (error.message || String(error))) || "Unknown error";
  const stackText = componentStack || (error && error.stack) || "";

  return (
    <div style={{ padding: "48px 24px", textAlign: "center", fontFamily: "system-ui", maxWidth: 880, margin: "0 auto" }}>
      <h1 style={{ fontSize: 22, marginBottom: 8 }}>Something broke on our end.</h1>
      <p style={{ color: "#64748B", marginBottom: 16 }}>
        We logged the error and someone will look at it. Please refresh the page.
      </p>
      <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 20 }}>
        <button
          onClick={() => (resetError ? resetError() : window.location.reload())}
          style={{ padding: "10px 20px", background: "#1D4ED8", color: "#fff", border: 0, cursor: "pointer", borderRadius: 6 }}
          data-testid="sentry-error-reload"
        >
          Reload
        </button>
        <button
          onClick={() => (window.location.href = "/owner")}
          style={{ padding: "10px 20px", background: "#fff", color: "#0F172A", border: "1px solid #E2E8F0", cursor: "pointer", borderRadius: 6 }}
        >
          Go to Overview
        </button>
      </div>

      {!isProd && (
        <details open style={{ textAlign: "left", background: "#FEF2F2", border: "1px solid #FECACA",
                               borderRadius: 8, padding: 12, fontFamily: "ui-monospace, monospace", fontSize: 12, color: "#7F1D1D" }}>
          <summary style={{ fontWeight: 600, cursor: "pointer", marginBottom: 6 }}>
            Error details (visible in dev / preview only)
          </summary>
          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", marginBottom: 6 }}>
            <b>Message:</b> {message}
          </div>
          {stackText && (
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 11, opacity: 0.85 }}>
              {String(stackText).slice(0, 4000)}
            </div>
          )}
          <div style={{ marginTop: 8, opacity: 0.75 }}>
            URL: <code>{typeof window !== "undefined" ? window.location.pathname + window.location.search : ""}</code>
          </div>
        </details>
      )}
    </div>
  );
}

async function bootstrap() {
  // Best-effort — never blocks the app if the endpoint is slow/unreachable.
  await Promise.race([
    initSentry(),
    new Promise(resolve => setTimeout(resolve, 1500)),
  ]);

  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <Sentry.ErrorBoundary
        fallback={({ error, componentStack, resetError }) => (
          <ErrorFallback error={error} componentStack={componentStack} resetError={resetError} />
        )}
      >
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </React.StrictMode>,
  );
}

bootstrap();
